/**
 * EmailRulesList — per-mailbox filter rules with toggle / edit / delete / reorder.
 */
import { h } from 'preact'
import { useState } from 'preact/hooks'

export function EmailRulesList({ store, i18n }) {
  const rules = store.rules.value
  const loading = store.rulesLoading.value
  const [dragId, setDragId] = useState(null)

  function summarizeCondition(rule) {
    const first = rule.conditions?.[0]
    if (!first) return '—'
    const field = i18n?.t(`emails.email_rules.conditions.fields.${first.field}`) || first.field
    const op = i18n?.t(`emails.email_rules.conditions.operators.${first.operator}`) || first.operator
    const extra = rule.conditions.length > 1 ? ` +${rule.conditions.length - 1}` : ''
    return `${field} ${op} "${(first.value || '').toString().slice(0, 40)}"${extra}`
  }

  function onDragStart(id) {
    setDragId(id)
  }

  async function onDrop(targetId) {
    if (dragId == null || dragId === targetId) return
    const ids = rules.map(r => r.id)
    const from = ids.indexOf(dragId)
    const to = ids.indexOf(targetId)
    if (from < 0 || to < 0) return
    ids.splice(from, 1)
    ids.splice(to, 0, dragId)
    setDragId(null)
    await store.reorderRules(ids)
  }

  if (loading) {
    return (
      <div class="text-muted py-5 text-center">
        <span class="spinner-border spinner-border-sm" />
      </div>
    )
  }

  if (rules.length === 0) {
    return (
      <div class="email-rules-empty">
        <div class="email-rules-empty-icon"><i class="bi bi-funnel" /></div>
        <div class="email-rules-empty-title">{i18n?.t('emails.email_rules.empty')}</div>
        <div class="email-rules-empty-hint">{i18n?.t('emails.email_rules.empty_hint')}</div>
        <button type="button" class="btn btn-primary" onClick={() => store.startNewRule()}>
          <i class="bi bi-plus-lg" /> {i18n?.t('emails.email_rules.new_title')}
        </button>
      </div>
    )
  }

  return (
    <div class="email-rules-list">
      <div class="email-rules-toolbar">
        <span class="text-muted small">
          {i18n?.t('emails.email_rules.count', { count: rules.length })}
        </span>
      </div>

      <div class="list-group shadow-sm rounded-2 overflow-hidden">
        {rules.map(rule => (
          <div
            key={rule.id}
            class={`list-group-item d-flex align-items-center gap-2 py-3 ${dragId === rule.id ? 'bg-light' : ''}`}
            draggable
            onDragStart={() => onDragStart(rule.id)}
            onDragOver={e => e.preventDefault()}
            onDrop={() => onDrop(rule.id)}
          >
            <span
              class="text-muted flex-shrink-0"
              style="cursor: grab"
              title={i18n?.t('emails.email_rules.drag_reorder')}
            >
              <i class="bi bi-grip-vertical" />
            </span>
            <div class="flex-grow-1 min-w-0">
              <div class="fw-semibold text-truncate">
                {rule.name}
                {rule.stop_processing && (
                  <span class="badge text-bg-secondary ms-2 fw-normal">stop</span>
                )}
                {!rule.active && (
                  <span class="badge text-bg-light border text-muted ms-2 fw-normal">
                    {i18n?.t('emails.email_rules.inactive') || 'inactive'}
                  </span>
                )}
              </div>
              <div class="small text-muted text-truncate mt-1">{summarizeCondition(rule)}</div>
              <div class="mt-2 d-flex flex-wrap gap-1">
                {(rule.actions || []).map((a, i) => (
                  <span key={i} class="badge bg-primary-subtle text-primary-emphasis fw-normal">
                    {i18n?.t(`emails.email_rules.actions.types.${a.type}`) || a.type}
                  </span>
                ))}
              </div>
            </div>
            <div class="d-flex align-items-center gap-1 flex-shrink-0">
              <button
                type="button"
                class={`btn btn-sm ${rule.active ? 'btn-success' : 'btn-outline-secondary'}`}
                onClick={() => store.toggleRule(rule.id)}
                title={i18n?.t('emails.email_rules.form.active_label')}
              >
                <i class={`bi ${rule.active ? 'bi-toggle-on' : 'bi-toggle-off'}`} />
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                onClick={() => store.startEditRule(rule.id)}
              >
                <i class="bi bi-pencil" />
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-danger"
                onClick={async () => {
                  if (!confirm(i18n?.t('emails.email_rules.delete_confirm'))) return
                  await store.deleteRule(rule.id)
                }}
              >
                <i class="bi bi-trash" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
